import type { ChartUnit } from '../../../types/chart-unit.js';
import { Formatter, TimeFormatter } from '../../../types/formatter.js';
import type { Point } from '../../../types/point.js';
import { AnyScale } from '../../../types/scales.js';
import { isBandScale } from '../../../utils/detect-scale.js';
import { scaleBandInvert } from '../../../utils/scale.js';

export type MagnifierFormatter = Formatter | TimeFormatter;

const DEFAULT_MAGNIFIER_PRECISION = 0;

function isDuration(args: {
  unit: ChartUnit;
  value: Date | number;
}): args is { unit: 'duration'; value: number } {
  return args.unit === 'duration';
}

function getMagnifierPrecision(scale: AnyScale): number {
  const [minDomain, maxDomain] = scale.domain();
  const [minRange, maxRange] = scale.range();

  const domainSpan = Math.abs(Number(maxDomain) - Number(minDomain));
  const rangeSpan = Math.abs(Number(maxRange) - Number(minRange));

  if (
    domainSpan === 0 ||
    rangeSpan === 0 ||
    !isFinite(domainSpan) ||
    !isFinite(rangeSpan)
  ) {
    return DEFAULT_MAGNIFIER_PRECISION;
  }

  const valuePerPixel = domainSpan / rangeSpan;

  // Calculates decimal precision based on valuePerPixel: how many data units each pixel represents.
  // Adapts precision so that displayed values reflect the actual data resolution visible on screen.
  return Math.max(
    DEFAULT_MAGNIFIER_PRECISION,
    Math.ceil(-Math.log10(valuePerPixel)),
  );
}

export function getMagnifierLabel(
  unit: ChartUnit,
  coordinate: 'y' | 'x',
  formatter: MagnifierFormatter,
  mousePosition?: Point,
  scale?: AnyScale,
): string | undefined {
  if (!mousePosition || !scale) {
    return;
  }

  if (isBandScale(scale)) {
    return scaleBandInvert(scale)(mousePosition[coordinate]);
  }

  let value = scale.invert(mousePosition[coordinate]);

  const measurement = { unit, value };
  if (isDuration(measurement)) {
    value = Math.round(measurement.value);
  } else if (typeof value === 'number') {
    const precision = getMagnifierPrecision(scale);
    const factor = Math.pow(10, precision);
    value = Math.round(value * factor) / factor;
  }

  return formatter(value as number & Date, unit);
}
